
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, CalendarClock } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format, parseISO } from 'date-fns';
import { ClassType, BookingType } from "@/lib/supabase/types";
import { fetchClasses } from "@/lib/supabase";
import { toast } from "sonner";

interface RescheduleBookingDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  booking: BookingType | null;
  onReschedule: (bookingId: string, oldClassId: string, newClassId: string) => Promise<void>;
}

const RescheduleBookingDialog: React.FC<RescheduleBookingDialogProps> = ({
  isOpen,
  onOpenChange,
  booking,
  onReschedule,
}) => {
  const [classes, setClasses] = useState<ClassType[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadClasses = async () => {
      if (isOpen && booking) {
        setIsLoading(true);
        try {
          const availableClasses = await fetchClasses();
          // Filter out the current class and classes with insufficient spots
          const groupSize = booking.groupSize || 1;
          const filteredClasses = availableClasses
            .filter(cls => 
              cls.id !== booking.classId && // Not the same class
              cls.availableSpots >= groupSize // Enough spots available
            )
            .sort((a, b) => {
              if (a.date && b.date) {
                return new Date(a.date).getTime() - new Date(b.date).getTime();
              }
              return 0;
            });
            
          setClasses(filteredClasses);
          setSelectedClassId(null);
          setError(null);
        } catch (err: any) {
          setError(err.message || "Failed to load available classes");
          setClasses([]);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    loadClasses();
  }, [isOpen, booking]);

  const handleReschedule = async () => {
    if (!booking || !selectedClassId) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      await onReschedule(booking.id, booking.classId, selectedClassId);
      onOpenChange(false);
    } catch (err: any) {
      console.error("Error during reschedule:", err);
      setError(err.message || "Failed to reschedule booking");
      toast.error(err.message || "Failed to reschedule booking");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!booking) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Reschedule Booking</DialogTitle>
          <DialogDescription>
            Reschedule booking for {booking.firstName} {booking.lastName} to a different class.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="mb-4 p-4 border rounded-md bg-muted/30">
            <h3 className="font-medium mb-2">Current Booking</h3>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <CalendarClock size={16} className="text-primary" />
                <span>{booking.className || 'Pottery Class'}</span>
                <span className="text-muted-foreground">on</span>
                <span>
                  {booking.classDate
                    ? format(parseISO(booking.classDate), 'EEEE, MMMM d, yyyy')
                    : booking.classDay}
                </span>
                <span className="text-muted-foreground">at</span>
                <span>{booking.classTime}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Group size:</span>
                <span>{booking.groupSize || 1}</span>
              </div>
            </div>
          </div>

          <div className="my-4">
            <h3 className="font-medium mb-2">Select New Class</h3>
            
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {isLoading ? (
              <div className="text-center py-8">Loading available classes...</div>
            ) : classes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No suitable classes available. Please check back later or create new classes.
              </div>
            ) : (
              <ScrollArea className="h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead></TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Available Spots</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {classes.map((cls) => (
                      <TableRow 
                        key={cls.id} 
                        className={selectedClassId === cls.id ? "bg-primary/10" : ""}
                        onClick={() => setSelectedClassId(cls.id)}
                      >
                        <TableCell>
                          <input 
                            type="radio" 
                            checked={selectedClassId === cls.id}
                            onChange={() => setSelectedClassId(cls.id)}
                            className="h-4 w-4"
                          />
                        </TableCell>
                        <TableCell>{cls.title}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar size={16} />
                            {cls.date ? format(parseISO(cls.date), 'EEE, MMM d, yyyy') : cls.day}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Clock size={16} />
                            {cls.time}
                          </div>
                        </TableCell>
                        <TableCell>{cls.availableSpots}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={handleReschedule}
            disabled={!selectedClassId || isLoading || isSubmitting}
          >
            {isSubmitting ? "Rescheduling..." : "Reschedule Booking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RescheduleBookingDialog;
